import { useCallback, useMemo, useState } from "react";
import { authAxios } from "../interceptors/axios";
import {
  getStepUpFailureMessage,
  getStepUpSuccessMessage,
  getStepUpThrottleMessage,
  getStepUpRequirement,
  isStepUpRequiredError,
  parseRetryAfter,
} from "../utils/stepUpUtils";

const initialState = {
  isOpen: false,
  status: "closed",
  requiredStrength: "password",
  reason: "",
  currentStrength: null,
  authAgeSeconds: null,
  maxAuthAgeSeconds: null,
  operation: null,
  actionLabel: "",
  error: "",
  retryAfterSeconds: null,
  successMessage: "",
};

function flattenStepUpError(errorPayload, fallback) {
  if (typeof errorPayload === "string") {
    return errorPayload;
  }

  if (errorPayload && typeof errorPayload === "object") {
    const message = Object.values(errorPayload)
      .flat()
      .filter(Boolean)
      .join(" ");
    if (message) {
      return message;
    }
  }

  return fallback;
}

export function useStepUpDialog() {
  const [state, setState] = useState(initialState);

  const requestStepUp = useCallback((error, actionLabel) => {
    const requirement = getStepUpRequirement(error);

    setState({
      isOpen: true,
      status: "required",
      requiredStrength: requirement.requiredStrength,
      reason: requirement.reason,
      currentStrength: requirement.currentStrength,
      authAgeSeconds: requirement.authAgeSeconds,
      maxAuthAgeSeconds: requirement.maxAuthAgeSeconds,
      operation: requirement.operation,
      actionLabel: actionLabel || requirement.operation || "this action",
      error: "",
      retryAfterSeconds: null,
      successMessage: "",
    });

    return requirement;
  }, []);

  const closeStepUp = useCallback(() => {
    setState(initialState);
  }, []);

  // A recovery code (AUTH-B) stands in for the authenticator code.
  const submitStepUp = useCallback(async ({ currentPassword, otp, recoveryCode }) => {
    setState((current) => ({
      ...current,
      status: "submitting",
      error: "",
      retryAfterSeconds: null,
    }));

    try {
      const payload = {
        current_password: currentPassword,
      };

      // Gait wants a second factor for an "mfa" step-up, and for any step-up
      // on an account with two-step on; send whichever one was entered.
      if (recoveryCode) {
        payload.recovery_code = recoveryCode.trim();
      } else if (otp || state.requiredStrength === "mfa") {
        payload.otp = otp;
      }

      const { data } = await authAxios.post("/reauthenticate/", payload);

      const successMessage = getStepUpSuccessMessage(state.actionLabel);
      setState((current) => ({
        ...current,
        status: "success",
        error: "",
        retryAfterSeconds: null,
        successMessage: data?.message || successMessage,
      }));

      return { ok: true, data };
    } catch (error) {
      if (error.response?.status === 429) {
        const retryAfterSeconds = parseRetryAfter(error.response.headers?.["retry-after"]);
        setState((current) => ({
          ...current,
          status: "throttled",
          error: getStepUpThrottleMessage(retryAfterSeconds),
          retryAfterSeconds,
          successMessage: "",
        }));
        return { ok: false, throttled: true, retryAfterSeconds };
      }

      if (isStepUpRequiredError(error)) {
        const requirement = getStepUpRequirement(error, state.requiredStrength);
        setState((current) => ({
          ...current,
          status: "required",
          requiredStrength: requirement.requiredStrength,
          reason: requirement.reason,
          currentStrength: requirement.currentStrength,
          authAgeSeconds: requirement.authAgeSeconds,
          maxAuthAgeSeconds: requirement.maxAuthAgeSeconds,
          operation: requirement.operation,
          error: "",
          retryAfterSeconds: null,
        }));
        return { ok: false, stepUpRequired: true, requirement };
      }

      const fallbackMessage = getStepUpFailureMessage(state.requiredStrength);
      setState((current) => ({
        ...current,
        status: "failed",
        error: flattenStepUpError(
          error.response?.data?.error || error.response?.data?.detail,
          fallbackMessage
        ),
        retryAfterSeconds: null,
        successMessage: "",
      }));
      return { ok: false, error };
    }
  }, [state.actionLabel, state.requiredStrength]);

  const derivedState = useMemo(() => state, [state]);

  return {
    state: derivedState,
    requestStepUp,
    submitStepUp,
    closeStepUp,
  };
}
